import {DestroyRef, inject, Injectable, signal} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {catchError, first, map, Observable, throwError} from 'rxjs';
import {CookieService} from 'ngx-cookie-service';
import {environment} from '../../../../environments/environment';
import {Consent, ICodeRequest, OAuthParams, RequestAuthorize} from '../../interfaces';
import {Router} from "@angular/router";
import {MaxInitResponse} from "../../interfaces/max/requestMax.interface";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private authParams = signal<OAuthParams>({client_id: '', redirect_uri: '', scope: ''});
  private destroy = inject(DestroyRef)
  private consentStatus = signal<Consent>({
    fieldConsent: null,
    confirmConsent: null
  });

  constructor(private http: HttpClient, private cookieService: CookieService, private router: Router) {
  }


  setConsentStatus(data: any) {
    this.consentStatus.update((x) => ({...x, ...data}));
  }

  getConsentStatus() {
    return this.consentStatus();
  }

  setAuthParams(params: OAuthParams) {
    this.authParams.set(params);
  }

  getCurrentValue() {
    const value = this.authParams();
    return value;
  }

  hasEditOption(): boolean {
    const params = this.authParams()
    return Array.isArray(params.options?.trim().split(/\s+/)) && params.options?.trim().split(/\s+/).includes('edit');
  }


  getScopesForConfirm(): Observable<any> {
    const params = this.authParams();
    if (!this.authParams()?.client_id || !this.authParams().scope || this.authParams() === null) {
      return throwError(() => new Error('Client ID is missing'));
    }

    const url = `${environment.ApiUrl}/api/v1.0/person/self/oauth/consent/inconsistent`;
    const queryParams = {client_id: params.client_id, scopes: params.scope.split(' ').join(',')};

    return this.http.get(url, {params: queryParams}).pipe(
      catchError((error) => {
        console.error('Failed to fetch inconsistent scopes:', error);
        return throwError(() => error);
      })
    );
  }

  confirmForScopes(scope: string[]): Observable<any> {
    const params = this.authParams();
    if (!params?.client_id) {
      return throwError(() => new Error('Client ID is missing'));
    }

    const url = `${environment.ApiUrl}/api/v1.0/person/self/oauth/consent`;
    const body = {client_id: params.client_id, scopes: scope};

    return this.http.post(url, body).pipe(
      catchError((error) => {
        console.error('Failed to confirm consent:', error);
        return throwError(() => error);
      })
    );
  }

  checkIfRequiredConsetsFilled(): Observable<any> {
    const params = this.authParams();
    if (!params?.client_id) {
      return throwError(() => new Error('Client ID is missing'));
    }

    const url = `${environment.ApiUrl}/api/v1.0/person/self/oauth/scope/dependency`;
    const queryParams = {scopes: this.authParams().scope.split(' ').join(',')};

    return this.http.get(url, {params: queryParams}).pipe(
      catchError((error) => {
        console.error('Failed to fetch scope dependencies:', error);
        return throwError(() => error);
      })
    );
  }

  getVerificationCode(requestBody: ICodeRequest): Observable<any> {
    return this.http
      .post(
        `${environment.ApiUrl}/api/v1.0/auth/login/by-code/sms/check/init`,
        requestBody,
        {observe: 'response'}
      )
      .pipe(
        catchError((error) => {
          console.error('Failed to get verification code:', error);
          return throwError(() => error);
        })
      );
  }


  getVerificationCodeMax(requestBody: ICodeRequest) {
    return this.http
      .post<MaxInitResponse>(
        `${environment.ApiUrl}/api/v1.0/auth/login/by-code/max/check/init`,
        requestBody,
        {observe: 'response'}
      )
      .pipe(
        catchError((error) => {
          console.error('Failed to get verification code:', error);
          return throwError(() => error);
        })
      );
  }


  sendVerificationCode(phoneNumber: string, code: string,channel: boolean): Observable<any> {
    const authParams = this.authParams();

    if (!authParams?.client_id) {
      return throwError(() => new Error('Client ID is missing'));
    }

    let params = new HttpParams()
      .set('grant_type', environment.auth.grantTypeAccess)
      .set('client_id', 'frontend')
      .set('redirect_uri', environment.ApiUrl)
      .set('username', phoneNumber)
      .set('secret_code', code)
      .set('scope', environment.auth.scope)
    if (channel) {
      params = params.set('channel', 'max');
    }
    return this.http
      .post(`${environment.ApiUrl}/token`, params, {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
      })
      .pipe(
        map((response: any) => {
          this.storeTokens(response);
          return response;
        }),
        catchError((error) => {
          console.error('Failed to send verification code:', error);
          return throwError(() => error);
        })
      );
  }

  refreshToken(): Observable<any> {
    const authParams = this.authParams();
    const refreshToken = this.cookieService.get('refresh_token');
    if (!refreshToken) {
      return throwError(() => new Error('Отсутствует refresh_token'));
    }

    if (!authParams?.client_id) {
      return throwError(() => new Error('Client ID is missing'));
    }

    const body = new HttpParams()
      .set('grant_type', environment.auth.grantTypeRefresh)
      .set('client_secret', 'ku75gVq1LfewJkB7X9zr4LW65jGf')
      .set('client_id', 'frontend')
      .set('refresh_token', refreshToken)
      .set('redirect_uri', authParams.redirect_uri);

    return this.http
      .post(`${environment.ApiUrl}/token`, body.toString(), {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
      })
      .pipe(
        map((response: any) => {
          this.storeTokens(response);
          return response;
        }),
        catchError((error) => {
          console.error('Failed to refresh token:', error);
          return throwError(() => error);
        })
      );
  }

  private storeTokens(response: any): void {
    const expiresIn = response.expires_in || 600;
    this.setToken('access_token', response.access_token, expiresIn);
    this.setToken('refresh_token', response.refresh_token);
  }

  private setToken(name: string, token: string, expiresIn?: number): void {
    if (expiresIn) {
      const expirationDate = new Date();
      expirationDate.setSeconds(expirationDate.getSeconds() + expiresIn);
      this.cookieService.set(name, token, {
        path: '/',
        expires: expirationDate,
      });
    } else {
      this.cookieService.set(name, token, {path: '/'});
    }
  }

  getToken(): string | null {
    return this.cookieService.get('access_token');
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }
  logout(): void {
    this.cookieService.delete('access_token');
    this.cookieService.delete('refresh_token');
  }

  redirectToMain() {
    const params = this.authParams();

    if (params?.redirect_uri && params.client_id) {
      const queryString = new URLSearchParams({
        response_type: 'code',
        client_id: params.client_id,
        redirect_uri: params.redirect_uri,
        scope: params.scope,
        state: params.state ?? '',
        code_challenge: params.code_challenge ?? '',
        code_challenge_method: params.code_challenge_method ?? '',
        options: params.options ?? '',
      }).toString();

      const redirectUrl = `/login?${queryString}`;
      window.location.href = redirectUrl;
    } else {
      console.warn('Недостаточно данных для возврата, выполняется fallback logout');
      this.router.navigate(['/login']);
    }
  }

  redirectToAuth(): void {
    this.logout();
    this.redirectToMain();
  }

  authorize(): void {
    const authParams = this.authParams();

    if (!authParams?.client_id || !authParams?.scope) {
      throw new Error('Client ID or Scope is missing');
    }

    let params = new HttpParams()
      .set('response_type', 'code')
      .set('client_id', authParams.client_id)
      .set('redirect_uri', authParams.redirect_uri)
      .set('scope', authParams.scope);

    if (authParams.code_challenge) {
      params = params.set("code_challenge", authParams.code_challenge);
    }
    if (authParams.code_challenge_method) {
      params = params.set("code_challenge_method", authParams.code_challenge_method);
    }
    if (authParams.state) {
      params = params.set("state", authParams.state);
    }
    this.http
      .get<RequestAuthorize>(`${environment.ApiUrl}/authorize`, {params})
      .pipe(first())
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: (response) => {
          if (response?.headers.Location) {
             window.location.href = response?.headers.Location;
          }
        },
      });
  }
}
